import { BaseWidgetProps, Device, ConsumptionChartBy } from "types";

import { BlueText } from "components/common";
import ChartByTime, { TimeRangeConfig } from "components/dashboard/ChartByTime";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { BarChart, BarSeriesOption } from "echarts/charts";
import { ChartAttribute } from "generated/models";
import useChartWidth from "hooks/useChartWidth";
import { cloneDeep, isEmpty, set } from "lodash";
import moment, { unitOfTime } from "moment";
import { useEffect, useRef, useState } from "react";
import dashboardService from "service/dashboardService";
import styledConst from "styles";
import { BarChartSeries } from "types/Charts";
import { getAPIError, showErrorAlert } from "utils/alert";
import {
    addDecimalPlace,
    isHttpSuccess,
    parseColorToHex,
} from "utils/functions";
import WidgetBox, { CustomSpinner } from "../WidgetBox";
import { dateTimeFormatter } from "utils/dateTimeFormatter";
import {
    detectGroupBy,
    getPeriodCurrentLastTime,
    getPeriodTimeRange,
} from "utils/chart";
import { ECharts } from "echarts";
import { CanvasRenderer } from "echarts/renderers";
import { ConsumptionAverageText } from "constant/chart";
import { fetchDetailDevices } from "service/gatewayService";
import { getPSLUsageMetric } from "service/proqueryService";
import { SensorDetails } from "types/Sensors";
import styled from "@emotion/styled";

echarts.use([BarChart, CanvasRenderer]);

const makeDeviceGatewayKey = (deviceId: string, gatewayId: string) =>
    `${deviceId}::${gatewayId}`;

/**
 * Calculates the total and average values of series data.
 * @param series Series containing data to be calculated.
 * @returns Returns an object containing the total and average values.
 */
export const getAggregateData = (
    total: number | null,
    series: BarChartSeries[]
): { average: number | null } => {
    if (total === null) {
        return {
            average: null,
        };
    }

    const seriesLengths = series.map((s) => s?.data?.length || 0);
    const numberOfDates = Math.max(...seriesLengths);
    const datesWithData = new Set<number>();

    for (let dateIndex = 0; dateIndex < numberOfDates; dateIndex++) {
        for (const element of series) {
            const value = element.data[dateIndex];
            if (typeof value === "number" && !Number.isNaN(value)) {
                datesWithData.add(dateIndex);
                break;
            }
        }
    }

    return {
        average: datesWithData.size > 0 ? total / datesWithData.size : null,
    };
};

const PeriodUnitMapping: { [key in ConsumptionChartBy]: { unit: unitOfTime.DurationAs } } = {
    [ConsumptionChartBy.Day]: { unit: "hours" },
    [ConsumptionChartBy.Week]: { unit: "hours" },
    [ConsumptionChartBy.Month]: { unit: "hours" },
    [ConsumptionChartBy.Year]: { unit: "hours" },
};

type EChartsOption = echarts.ComposeOption<BarSeriesOption>;

const ChartOptions: EChartsOption = {
    responsive: true,
    backgroundColor: "#2F3A51",
    textStyle: {
        color: "#ffffff",
        fontFamily: "Roboto",
        fontWeight: 400,
        overflow: "truncate",
        ellipsis: "...",
    },
    title: {
        show: true,
        left: 14,
        top: 15,
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
            overflow: "truncate",
            ellipsis: "...",
        },
        subtextStyle: {
            color: styledConst.Primary_Blue_4,
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: 14,
            overflow: "truncate",
            ellipsis: "...",
            rich: {
                s1: {
                    color: styledConst.Primary_Blue_4,
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: 20,
                },
                s2: {
                    fontSize: 14,
                    color: styledConst.Primary_Green_1,
                    lineHeight: 20,
                },
                s3: {
                    color: "#ffffff",
                    fontSize: 14,
                    fontFamily: "Roboto",
                },
                s4: {
                    color: styledConst.Primary_Blue_4,
                    fontSize: 14,
                    fontWeight: 500,
                },
                s5: {
                    color: "#F2495E",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: 18,
                    lineHeight: 22,
                },
                s6: {
                    color: "#00CBAF",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: 18,
                    lineHeight: 22,
                },
            },
        },
    },
    legend: {
        type: "scroll",
        selectedMode: true,
        left: 15,
        bottom: 12,
        textStyle: {
            color: "#ffffff",
            fontFamily: "Roboto",
            fontWeight: 400,
        },
        icon: "circle",
        pageTextStyle: {
            color: "#ffffff",
        },
        pageIconInactiveColor: "#6c6d6e",
        pageIconColor: "#c8c6c6",
        show: true,
    },
    tooltip: {
        trigger: "axis",
        renderMode: "html",
        appendTo: "body",
    },
    toolbox: {
        show: false,
    },
    grid: {
        top: 165,
        left: "3%",
        right: "4%",
        bottom: 60,
        containLabel: true,
    },
    xAxis: {
        type: "category",
        axisTick: {
            alignWithLabel: true,
        },
    },
    yAxis: {
        type: "value",
    },
};

// Extract tooltip formatter function to reduce nesting
const createTooltipFormatter = (
    getConsumptionTimeFormat: (name: string) => string,
    unit: unitOfTime.DurationAs
) => {
    return (params: any) => {
        const { name } = params[0];
        let series = ``;
        params.forEach(
            ({ seriesName, value, marker }: any) =>
            (series += `<span class="d-flex justify-content-between" >
                    <span>${marker}${seriesName}</span>
                    <span class="ml-2 font-weight-bold">${getDurationString(value, unit)}</span>
                </span>
                `)
        );
        return `
                    ${getConsumptionTimeFormat(name)} <br />
                    ${series}
                `;
    };
};

// Extract Y-axis formatter function to reduce nesting
const createYAxisFormatter = (accuracy: string) => {
    return (value: string) => {
        return Number(value) > 10 ? addDecimalPlace(value, accuracy) : value;
    };
};

// Extract X-axis formatter function to reduce nesting
const createXAxisFormatter = (
    getConsumptionTimeFormat: (value: string, isLabel: boolean) => string
) => {
    return (value: string) => getConsumptionTimeFormat(value, true);
};

// Extract chart ready handler to reduce nesting
const createChartReadyHandler = (
    setSelectedLegend: (selected: Record<string, boolean>) => void
) => {
    return (chartInstance: any) => {
        chartInstance.on("legendselectchanged", function (changes: any) {
            setSelectedLegend(changes.selected);
        });
    };
};
const getDurationString = (
    duration: number | string,
    unit: unitOfTime.DurationAs = "seconds"
) => {
    if (duration === null || duration === undefined || Number.isNaN(Number(duration))) {
        return `-`;
    }

    const durationTime = moment.duration(Number(duration), unit);
    const totalMinutes = durationTime.asMinutes();
    const wholeHours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    let result = "";
    if (wholeHours > 0) {
        result += `${wholeHours} h `;
    }
    if (minutes > 0) {
        result += `${minutes} m`;
    }

    return result || "0 m";
};

const FilterWrapper = styled.div`
    position: absolute;
    right: 20px;
    top: 50px;
    z-index: 1;
`;

const UsageWidget = (props: BaseWidgetProps) => {
    // ----------------- props -----------------
    const { panel, isVisible, visibleRef, zoomLevel, dashboardId } = props;

    const {
        query_time = null,
        time_range = "",
        attributes = [],
        uuid: panelId = "",
        name = "",
    } = panel;

    const chartRef = useRef(null as any);
    const chartColor = attributes[0].color;

    // ----------------- chart -----------------
    const [options, setOptions] = useState<EChartsOption>(ChartOptions);
    const [chartLoading, setChartLoading] = useState(true);
    const [devices, setDevices] = useState<Device[]>([]);
    const [avg, setAvg] = useState("");
    const [groupBy, setGroupBy] = useState(ConsumptionChartBy.Day);
    const [timeRange, setTimeRange] = useState<TimeRangeConfig>({
        timeRange: "1d",
        lastTime: moment(),
    });
    const [selectedLegend, setSelectedLegend] = useState<
        Record<string, boolean>
    >({});

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 250,
    });

    const titleThreshold = 150;
    const chartInstance: ECharts = chartRef.current?.getEchartsInstance();
    const isMultiple = attributes.length > 1;

    // ! ----------------- functions -----------------

    const getNewOptions = (baseOption: EChartsOption, newConfigArr: any[]) => {
        const newOption = cloneDeep(baseOption); // immutable
        newConfigArr.forEach(({ path, value }: any) =>
            set(newOption, path, value)
        );

        return newOption;
    };

    const getTimeFormat = (value: string, isLabel = false) => {
        const lastTime = timeRange.lastTime;

        return dateTimeFormatter.chart(value, groupBy, isLabel, lastTime);
    };

    const getRoomName = (attributes: ChartAttribute[]) => {
        return isMultiple
            ? `{s2|${attributes.length} Rooms}`
            : `{s1|Room}: {s3|${devices?.[0]?.deviceName ?? "Unknown"}}`;
    };

    const fetchNewData = async () => {
        setChartLoading(true);
        const devicesList: SensorDetails[] = [];

        attributes.forEach((attr: ChartAttribute) => {
            devicesList.push({
                device_id: attr.device_id,
                gateway_id: attr.gateway_id,
                said: 0,
                name: "Room ON Time",
                color: parseColorToHex(chartColor),
            });
        });

        const { series, categories, total } = await getPSLUsageMetric({
            devices: devicesList,
            groupBy,
            queryTime: timeRange.lastTime.unix(),
            colors: [chartColor],
        });

        if (!Array.isArray(series)) {
            setChartLoading(false);
            return;
        }

        const { average } = getAggregateData(total[0], [
            series[0] ?? { data: [] },
        ]);

        const _selectedLegend = isEmpty(selectedLegend)
            ? series.reduce((acc: any, { name }: { name: string }) => {
                acc[name] = true; // default to true for all series
                return acc;
            }, {})
            : selectedLegend;

        const _options = [
            {
                path: "xAxis.data",
                value: categories,
            },
            {
                path: "xAxis.axisLabel.formatter",
                value: createXAxisFormatter(getTimeFormat),
            },
            {
                path: "yAxis.axisLabel.formatter",
                value: createYAxisFormatter("0"),
            },
            {
                path: "series",
                value: series.map((s, index) => ({
                    ...s,
                    name: "Room Occupancy",
                    barWidth: "60%",
                    color: chartColor
                        ? parseColorToHex(chartColor)
                        : s.color,
                })),
            },
            {
                path: "legend.selected",
                value: _selectedLegend,
            },
            {
                path: "title.subtext",
                value: `{s1|Room Usage}\n${getRoomName(
                    attributes
                )}\n{s5|${getDurationString(
                    total[0] ?? 0
                )}} {s4|(Total Room Occupancy)}`,
            },
            {
                path: "title.text",
                value: name,
            },
            {
                path: "title.textStyle.width",
                value: boxWidth + titleThreshold,
            },
            {
                path: "textStyle.width",
                value: boxWidth + titleThreshold,
            },
            {
                path: "title.subtextStyle.width",
                value: boxWidth,
            },
            {
                path: "tooltip.formatter",
                value: createTooltipFormatter(
                    getTimeFormat,
                    PeriodUnitMapping[groupBy].unit,
                ),
            },
            {
                path: "title.subtextStyle.rich.s5.color",
                value: parseColorToHex(chartColor),
            },
            {
                path: "legend.width",
                value: boxWidth + 60,
            },
        ];

        const newOption = getNewOptions(options, _options);
        setAvg(
            getDurationString(
                moment
                    .duration(average ?? 0, "seconds")
                    .as(PeriodUnitMapping[groupBy].unit),
                PeriodUnitMapping[groupBy].unit
            )
        );
        setOptions(newOption);
        setChartLoading(false);
    };

    // ! ----------------- useEffect -----------------

    useEffect(() => {
        if (!isEmpty(devices) && isVisible) {
            fetchNewData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, devices, timeRange]);

    useEffect(() => {
        if (!isVisible || !isEmpty(devices)) return;

        const attributeKeySet = new Set(
            attributes.map((attr: any) =>
                makeDeviceGatewayKey(attr.device_id, attr.gateway_id)
            )
        );

        void (async () => {
            const res = await fetchDetailDevices(attributes);
            const networkList = res || [];
            setDevices(
                networkList.filter((network: Device) =>
                    attributeKeySet.has(
                        makeDeviceGatewayKey(network.deviceId, network.gatewayId)
                    )
                )
            );
        })();
    }, [attributes, isVisible, devices]);

    useEffect(() => {
        const groupBy = detectGroupBy(time_range);
        const lastTime = query_time
            ? moment(query_time)
            : getPeriodCurrentLastTime(groupBy);

        setTimeRange({
            timeRange: time_range,
            lastTime: lastTime,
        });

        setGroupBy(groupBy);
    }, [panelId, query_time, time_range]);

    useEffect(() => {
        chartInstance?.setOption({
            title: {
                textStyle: {
                    width: boxWidth + titleThreshold,
                },
                subtextStyle: {
                    width: boxWidth,
                },
            },
            legend: {
                width: boxWidth + 60,
            },
        });
    }, [chartInstance, boxWidth]);

    const onTimeChange = async (
        timeRange: TimeRangeConfig,
        groupBy: ConsumptionChartBy
    ) => {
        setTimeRange(timeRange);
        setGroupBy(groupBy);

        const panelRes = await dashboardService.updatePanel(
            dashboardId,
            panelId,
            {
                time_range: getPeriodTimeRange(groupBy),
            }
        );
        if (!isHttpSuccess(panelRes.status)) {
            showErrorAlert(
                getAPIError(
                    panelRes,
                    "Something went wrong when changing the time range. Please try again."
                )
            );
        }
    };

    const chartOptions = options;

    // ! ----------------- render -----------------

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget position-relative d-flex">
                <FilterWrapper>
                    <ChartByTime
                        timeRange={timeRange}
                        onChange={onTimeChange}
                        view={groupBy}
                    />
                </FilterWrapper>
                <div className="widget-tool">
                    <BlueText className="mr-1 refresh-drop font-weight-normal">
                        <span>{ConsumptionAverageText[groupBy]} Avg: </span>
                        <span className="text-white">{avg}</span>
                    </BlueText>
                </div>
                {!chartLoading && (
                    <ReactECharts
                        ref={chartRef}
                        className="chart-box w-100 h-100"
                        theme={"dark"}
                        option={chartOptions}
                        lazyUpdate={true}
                        notMerge={true}
                        onChartReady={createChartReadyHandler(
                            setSelectedLegend
                        )}
                    />
                )}
                {chartLoading && (
                    <div className="d-flex justify-content-center align-items-center mt-n5 flex-fill">
                        <CustomSpinner animation="border" />
                    </div>
                )}
            </div>
        </WidgetBox>
    );
};

export default UsageWidget;
